import { formatToDateAndTime } from '@zendesk/forethought-elements';
import i18n from 'src/locales';
import {
  CreateOrUpdateSolveABTestConfig,
  MAX_WINNER_METRICS,
  SolveABTestConfig,
  SolveABTestVariant,
  VARIANT_DEFAULT_NAMES,
} from 'src/services/agent-ab-test/types';
import { WorkflowVersionType } from 'src/slices/workflow-preview/types';
import { DateRange } from 'src/types/types';
import {
  AutoflowVersionHistorySummary,
  IntentData,
} from 'src/types/workflowBuilderAPITypes';

/**
 * Compute the true UTC instant for a naive datetime that represents wall-clock
 * time in the given IANA timezone.
 *
 * Approach: parse the digits as UTC, format that UTC instant in the target tz to
 * find the offset, then shift by the offset.  A single iteration is accurate
 * except right at a DST boundary where the offset itself changes between the
 * guess and the corrected instant – a second pass fixes that.
 */
export const naiveDatetimeToUtcInstant = (
  naiveDatetime: string,
  timezone: string,
): Date => {
  const isoString = naiveDatetime.endsWith('Z')
    ? naiveDatetime
    : naiveDatetime + 'Z';
  const asUtc = new Date(isoString);

  const getOffset = (d: Date): number => {
    const parts = new Intl.DateTimeFormat('en-US', {
      day: '2-digit',
      hour: '2-digit',
      hour12: false,
      minute: '2-digit',
      month: '2-digit',
      second: '2-digit',
      timeZone: timezone,
      year: 'numeric',
    }).formatToParts(d);
    const get = (type: string) =>
      parts.find(p => p.type === type)?.value ?? '0';
    const wall = new Date(
      `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}Z`,
    );
    return wall.getTime() - d.getTime();
  };

  // First pass: rough offset using the UTC-interpreted instant
  const offset1 = getOffset(asUtc);
  const corrected = new Date(asUtc.getTime() - offset1);

  // Second pass: refine in case the offset changed (DST boundary)
  const offset2 = getOffset(corrected);
  if (offset1 !== offset2) {
    return new Date(asUtc.getTime() - offset2);
  }
  return corrected;
};

/**
 * Format a naive datetime string from the API in the AB test config's timezone.
 *
 * The API stores start/end datetimes as naive strings that represent wall-clock
 * time in the config's timezone (e.g. "2026-03-26T17:15:00" in America/Toronto).
 * Passing these directly to `new Date()` / `formatToDateAndTime()` would
 * reinterpret them in the browser's local timezone, which is wrong when the
 * viewer is in a different timezone.
 *
 * This function converts the naive datetime to the correct UTC instant, then
 * formats it in the config's timezone with Intl.DateTimeFormat for correct
 * display including the right DST abbreviation.
 */
export const formatAbTestDatetime = (
  naiveDatetime: string,
  timezone?: string | null,
): string => {
  if (!naiveDatetime) {
    return '';
  }

  const tz = timezone || 'UTC';
  try {
    const utcInstant = naiveDatetimeToUtcInstant(naiveDatetime, tz);

    return new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      month: 'short',
      timeZone: tz,
      timeZoneName: 'short',
      year: 'numeric',
    }).format(utcInstant);
  } catch {
    // Invalid timezone string — fall back to the raw datetime value
    return naiveDatetime;
  }
};

/**
 * Constructs a CreateOrUpdateSolveABTestConfig object from an existing config
 * and new fields to update. Only includes fields that are part of the request type.
 */
export const generateAbTestConfigRequestBody = (
  config: SolveABTestConfig,
  fields: Partial<CreateOrUpdateSolveABTestConfig>,
): CreateOrUpdateSolveABTestConfig => {
  return {
    description: config.description,
    end_condition: config.end_condition,
    intent_workflow_id: config.intent_workflow_id,
    name: config.name,
    product: config.product,
    start_datetime: config.start_datetime,
    summary: config.summary,
    timezone: config.timezone ?? undefined,
    total_traffic_percentage: config.total_traffic_percentage,
    variants: config.variants,
    winner_metrics: config.winner_metrics,
    ...fields,
  };
};

export const getIsAgentEligibleForAbTestSelection = (intent: IntentData) => {
  return intent.is_autoflow;
};

export const hasValidVariants = (config: SolveABTestConfig) =>
  (config.variants ?? []).some(v => v.intent_workflow_version > 0 && v.name);

/**
 * Stable key representing the variant set, for use as a dependency
 * without triggering re-renders on unrelated config changes.
 */
export const getVariantsKey = (config: SolveABTestConfig) =>
  (config.variants ?? [])
    .filter(v => v.intent_workflow_version > 0 && v.name)
    .map(v => `${v.name}:${v.intent_workflow_version}:${v.version_type}`)
    .join(',');

export const getIsDetailSectionComplete = (config: SolveABTestConfig) => {
  return Boolean(
    config.name.trim() && config.description?.trim() && config.product,
  );
};

export const getIsTotalTrafficPercentageValid = (config: SolveABTestConfig) => {
  return (
    config.variants?.reduce(
      (acc, variant) => acc + (variant.traffic_percentage || 0),
      0,
    ) === 100
  );
};

export const getIsAgentsAndVariantsSectionComplete = (
  config: SolveABTestConfig,
) => {
  return Boolean(
    config.variants &&
    config.variants.length >= 2 &&
    config.variants.every(
      variant =>
        variant.intent_workflow_version && variant.traffic_percentage > 0,
    ) &&
    getIsTotalTrafficPercentageValid(config) &&
    new Set(
      config.variants.map(
        v => `${v.intent_workflow_version}:${v.version_type}`,
      ),
    ).size === config.variants.length,
  );
};

export const getIsDuplicateVariantVersion = (
  variant: SolveABTestVariant | undefined,
  otherVariants: (SolveABTestVariant | undefined)[],
): boolean => {
  if (!variant || variant.intent_workflow_version <= 0) {
    return false;
  }
  return otherVariants.some(
    other =>
      other &&
      other.intent_workflow_version === variant.intent_workflow_version &&
      other.version_type === variant.version_type,
  );
};

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export const getIsMinimumDurationMet = (
  startDatetime: string,
  endDatetime: string,
): boolean => {
  return (
    new Date(endDatetime).getTime() - new Date(startDatetime).getTime() >=
    ONE_DAY_MS
  );
};

export const getIsWinnerSectionComplete = (config: SolveABTestConfig) => {
  return Boolean(
    config.winner_metrics &&
    config.winner_metrics.length >= 1 &&
    config.winner_metrics.length <= MAX_WINNER_METRICS &&
    config.timezone &&
    config.end_condition &&
    config.start_datetime &&
    ((config.end_condition.condition_type === 'sample_count' &&
      config.end_condition.sample_count) ||
      (config.end_condition.condition_type === 'fixed_duration' &&
        config.end_condition.end_datetime &&
        getIsMinimumDurationMet(
          config.start_datetime,
          config.end_condition.end_datetime,
        ))),
  );
};

export const getAreAllSectionsComplete = (config: SolveABTestConfig) => {
  return (
    getIsDetailSectionComplete(config) &&
    getIsAgentsAndVariantsSectionComplete(config) &&
    getIsWinnerSectionComplete(config)
  );
};

export const getVersionHistoryDataForIntentWorkflowId = (
  intentWorkflowId: string,
  versionHistory: AutoflowVersionHistorySummary[],
) => {
  return versionHistory.find(
    v => v.is_published && v.intent_workflow_id === intentWorkflowId,
  );
};

export const generateVersionOptions = (
  versionHistory: AutoflowVersionHistorySummary[],
  intentTitle: string,
) => {
  const validVersions = versionHistory.filter(
    v => v.version_type === 'published' || v.version_type === 'saved',
  );

  return validVersions.map(version => ({
    category:
      version.version_type === 'published'
        ? i18n.t('agent-ab-test:versionLabels.publishedHistory')
        : i18n.t('agent-ab-test:versionLabels.versionsFor', {
            title: intentTitle,
          }),
    label:
      version.version_type === 'published'
        ? generatePublishedVersionLabel(version)
        : version.version_name,
    value: version.version.toString(),
  }));
};

export const getVariantVersionLabel = (
  variant: SolveABTestVariant,
  versionHistory: AutoflowVersionHistorySummary[],
): string => {
  const version = versionHistory.find(
    v =>
      v.version === variant.intent_workflow_version &&
      v.version_type === variant.version_type,
  );
  if (version?.is_published) {
    return i18n.t('agent-ab-test:versionLabels.live');
  }
  if (version?.version_type === 'auto_saved') {
    return i18n.t('agent-ab-test:versionLabels.autoSaved');
  }
  if (version?.version_name && version.version_name !== 'None') {
    return version.version_name;
  }
  return i18n.t('agent-ab-test:versionLabels.version', {
    version: variant.intent_workflow_version,
  });
};

export const generatePublishedVersionLabel = (
  versionHistoryItem: AutoflowVersionHistorySummary,
) => {
  return formatToDateAndTime(versionHistoryItem.last_updated_at);
};

/**
 * Validates and parses a traffic percentage value.
 * Returns the parsed number if valid (0-100), or null if invalid.
 */
export const parseTrafficPercentage = (value: string): number | null => {
  const parsed = Number(value);
  if (isNaN(parsed) || parsed < 0 || parsed > 100) {
    return null;
  }
  return parsed;
};

/**
 * Builds the updated variants array when the control version dropdown changes.
 * If a control variant already exists, updates it in place.
 * If no control variant exists (e.g. agent was pre-populated but variants are empty),
 * creates a new control variant and prepends it.
 */
export const buildControlVariantUpdate = (
  currentVariants: SolveABTestVariant[],
  version: number,
  versionType: WorkflowVersionType,
  fallbackTrafficPercentage = 50,
): SolveABTestVariant[] => {
  const hasControlVariant = currentVariants.some(v => v.is_control);

  if (hasControlVariant) {
    return currentVariants.map(v =>
      v.is_control
        ? {
            ...v,
            intent_workflow_version: version,
            name: VARIANT_DEFAULT_NAMES[0],
            version_type: versionType,
          }
        : v,
    );
  }

  return [
    {
      intent_workflow_version: version,
      is_control: true,
      name: VARIANT_DEFAULT_NAMES[0],
      traffic_percentage: fallbackTrafficPercentage,
      version_type: versionType,
    },
    ...currentVariants,
  ];
};

/**
 * Converts AB test config datetime strings + timezone to a DateRange
 * spanning the test duration. Uses naiveDatetimeToUtcInstant for
 * correct DST handling. Falls back to undefined if start date is absent.
 */
/**
 * Extract the YYYY-MM-DD date portion as it appears in the given timezone.
 */
const getDateInTimezone = (date: Date, timezone: string): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    day: '2-digit',
    month: '2-digit',
    timeZone: timezone,
    year: 'numeric',
  }).formatToParts(date);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}`;
};

export const getAbTestDateRange = (
  startDatetime?: string | null,
  endDatetime?: string | null,
  timezone?: string | null,
): DateRange | undefined => {
  if (!startDatetime || !timezone) {
    return undefined;
  }

  // Snap to start-of-day in the AB test's timezone
  const startInstant = naiveDatetimeToUtcInstant(startDatetime, timezone);
  const startDateStr = getDateInTimezone(startInstant, timezone);
  const startOfDay = naiveDatetimeToUtcInstant(
    `${startDateStr}T00:00:00`,
    timezone,
  );

  // Snap to end-of-day in the AB test's timezone
  let endInstant: Date;
  if (endDatetime) {
    endInstant = naiveDatetimeToUtcInstant(endDatetime, timezone);
  } else {
    endInstant = new Date();
  }
  const endDateStr = getDateInTimezone(endInstant, timezone);
  const endOfDay = naiveDatetimeToUtcInstant(
    `${endDateStr}T23:59:59`,
    timezone,
  );

  return { from: startOfDay, to: endOfDay };
};
